/*
  786 Motion System
  -----------------
  GSAP + ScrollTrigger + Lenis smooth scroll.
  Any element can opt in with data-anim="<type>" and optional modifiers:

    data-anim="fade-up | fade-in | fade-left | fade-right | scale-in | clip-up | clip-left | blur-in
               | words | chars | lines | stagger | parallax | pin-text | counter | marquee | reveal-img"
    data-anim-delay="0.2"        seconds
    data-anim-duration="1.2"     seconds
    data-anim-stagger="0.08"     seconds between children (for stagger/words/chars/lines)
    data-anim-start="top 85%"    ScrollTrigger start
    data-anim-once="false"       replay when scrolling back up (default true = once)
    data-anim-speed="0.3"        parallax strength (-1..1)
    data-anim-ease="power3.out"
    data-anim-scrub="true"       tie progress to scroll instead of playing once

  Hover helpers:
    data-magnetic            pulls element toward cursor
    data-tilt                3D tilt on hover (cards)
    data-hover-scale="1.04"  scale on hover

  Global:
    window.MOTION = { enabled, smoothScroll, intensity }  (set from Liquid)
    document.documentElement gets class "motion-ready" once JS is live.
*/
(function () {
  'use strict';

  var cfg = Object.assign(
    { enabled: true, smoothScroll: true, intensity: 1, cursor: false },
    window.MOTION || {}
  );

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isEditor = !!window.Shopify && !!window.Shopify.designMode;
  var isTouch = window.matchMedia('(hover: none)').matches;
  var html = document.documentElement;

  if (!cfg.enabled || reduce || typeof gsap === 'undefined') {
    html.classList.add('motion-off');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  html.classList.add('motion-ready');

  var I = Math.max(0.3, Math.min(2, parseFloat(cfg.intensity) || 1));
  var D = function (v, fb) { var n = parseFloat(v); return isNaN(n) ? fb : n; };

  /* ---------------------------------------------------------------- Lenis */
  var lenis = null;
  if (cfg.smoothScroll && !isEditor && !isTouch && typeof Lenis !== 'undefined') {
    lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1, smoothWheel: true });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
    gsap.ticker.lagSmoothing(0);
    html.classList.add('lenis-active');
    window.lenis = lenis;

    // anchor links
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a || a.getAttribute('href').length < 2) return;
      var t = document.querySelector(a.getAttribute('href'));
      if (t) { e.preventDefault(); lenis.scrollTo(t, { offset: -80 }); }
    });
    // stop scroll while drawers are open (theme toggles these attrs/classes)
    var mo = new MutationObserver(function () {
      var locked = document.body.classList.contains('scroll-lock') ||
        document.body.classList.contains('overflow-hidden') ||
        document.documentElement.hasAttribute('data-scroll-locked') ||
        document.body.getAttribute('data-fluorescent-overlay-open') === 'true' ||
        document.body.classList.contains('s786-menu-open');
      locked ? lenis.stop() : lenis.start();
    });
    mo.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-fluorescent-overlay-open'] });
    mo.observe(html, { attributes: true, attributeFilter: ['data-scroll-locked'] });
  }

  /* ------------------------------------------------------- text splitting */
  function splitText(el, mode) {
    if (el.dataset.split) return el.querySelectorAll('.m-unit');
    var text = el.textContent;
    el.setAttribute('aria-label', text.trim());
    el.dataset.split = mode;
    var frag = document.createDocumentFragment();
    if (mode === 'lines') {
      // approximate lines by <br> or sentences; real line-split needs layout, so wrap words and group later
      mode = 'words';
    }
    var parts = mode === 'chars' ? text.split('') : text.split(/(\s+)/);
    parts.forEach(function (p) {
      if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(' ')); return; }
      if (p === '') return;
      var wrap = document.createElement('span');
      wrap.className = 'm-mask';
      var unit = document.createElement('span');
      unit.className = 'm-unit';
      unit.textContent = p === ' ' ? ' ' : p;
      wrap.appendChild(unit);
      frag.appendChild(wrap);
    });
    el.textContent = '';
    el.appendChild(frag);
    return el.querySelectorAll('.m-unit');
  }

  /* ---------------------------------------------------------- animations */
  var presets = {
    'fade-up':    { from: { autoAlpha: 0, y: 48 * I },            to: { autoAlpha: 1, y: 0 } },
    'fade-in':    { from: { autoAlpha: 0 },                        to: { autoAlpha: 1 } },
    'fade-left':  { from: { autoAlpha: 0, x: -64 * I },           to: { autoAlpha: 1, x: 0 } },
    'fade-right': { from: { autoAlpha: 0, x: 64 * I },            to: { autoAlpha: 1, x: 0 } },
    'scale-in':   { from: { autoAlpha: 0, scale: 0.86 },          to: { autoAlpha: 1, scale: 1 } },
    'blur-in':    { from: { autoAlpha: 0, filter: 'blur(14px)', y: 20 }, to: { autoAlpha: 1, filter: 'blur(0px)', y: 0 } },
    'clip-up':    { from: { clipPath: 'inset(100% 0 0 0)', y: 30 }, to: { clipPath: 'inset(0% 0 0 0)', y: 0 } },
    'clip-left':  { from: { clipPath: 'inset(0 100% 0 0)' },      to: { clipPath: 'inset(0 0% 0 0)' } },
    'reveal-img': { from: { clipPath: 'inset(0 0 100% 0)', scale: 1.15 }, to: { clipPath: 'inset(0 0 0% 0)', scale: 1 } }
  };

  function trigger(el, extra) {
    var once = el.dataset.animOnce !== 'false';
    return Object.assign({
      trigger: el,
      start: el.dataset.animStart || 'top 88%',
      toggleActions: once ? 'play none none none' : 'play none none reverse'
    }, extra || {});
  }

  function animate(el) {
    if (el.dataset.animDone) return;
    el.dataset.animDone = '1';
    var type = el.dataset.anim;
    var delay = D(el.dataset.animDelay, 0);
    var dur = D(el.dataset.animDuration, 1.1);
    var ease = el.dataset.animEase || 'power3.out';
    var stagger = D(el.dataset.animStagger, 0.08);
    var scrub = el.dataset.animScrub === 'true';

    if (presets[type]) {
      var p = presets[type];
      gsap.fromTo(el, p.from, Object.assign({}, p.to, { duration: dur, delay: delay, ease: ease, scrollTrigger: trigger(el, scrub ? { scrub: 1, end: 'top 40%' } : null) }));
      return;
    }

    if (type === 'words' || type === 'chars' || type === 'lines') {
      var units = splitText(el, type);
      gsap.set(el, { autoAlpha: 1 });
      gsap.fromTo(units, { yPercent: 110, rotate: type === 'chars' ? 6 : 3 },
        { yPercent: 0, rotate: 0, duration: dur, ease: ease, delay: delay, stagger: type === 'chars' ? stagger / 2 : stagger, scrollTrigger: trigger(el) });
      return;
    }

    if (type === 'stagger') {
      var kids = el.dataset.animChildren ? el.querySelectorAll(el.dataset.animChildren) : el.children;
      var childType = el.dataset.animChild || 'fade-up';
      var cp = presets[childType] || presets['fade-up'];
      gsap.set(el, { autoAlpha: 1 });
      gsap.fromTo(kids, cp.from, Object.assign({}, cp.to, { duration: dur, ease: ease, delay: delay, stagger: { each: stagger, from: el.dataset.animFrom || 'start' }, scrollTrigger: trigger(el) }));
      return;
    }

    if (type === 'parallax') {
      var speed = D(el.dataset.animSpeed, 0.25) * I;
      gsap.set(el, { autoAlpha: 1 });
      gsap.fromTo(el, { yPercent: -speed * 40 }, { yPercent: speed * 40, ease: 'none',
        scrollTrigger: { trigger: el.parentElement || el, start: 'top bottom', end: 'bottom top', scrub: true } });
      return;
    }

    if (type === 'counter') {
      var target = D(el.dataset.animTo, parseFloat(el.textContent) || 0);
      var obj = { v: 0 };
      var suffix = el.dataset.animSuffix || '';
      gsap.set(el, { autoAlpha: 1 });
      gsap.to(obj, { v: target, duration: dur * 1.5, ease: 'power2.out', delay: delay,
        onUpdate: function () { el.textContent = Math.round(obj.v).toLocaleString() + suffix; },
        scrollTrigger: trigger(el) });
      return;
    }

    if (type === 'marquee') {
      var speedM = D(el.dataset.animSpeed, 40); // px per second
      var track = el.querySelector('[data-marquee-track]') || el.firstElementChild;
      if (!track) return;
      // duplicate content until wide enough
      var w = track.scrollWidth;
      while (track.scrollWidth < el.offsetWidth * 2 + w) track.innerHTML += track.innerHTML;
      var dist = track.scrollWidth / 2;
      gsap.set(el, { autoAlpha: 1 });
      var tween = gsap.to(track, { x: -dist, duration: dist / speedM, ease: 'none', repeat: -1 });
      el.addEventListener('mouseenter', function () { gsap.to(tween, { timeScale: 0.2, duration: 0.5 }); });
      el.addEventListener('mouseleave', function () { gsap.to(tween, { timeScale: 1, duration: 0.5 }); });
      return;
    }

    if (type === 'pin-text') {
      // pins the element while its parent scrolls
      gsap.set(el, { autoAlpha: 1 });
      ScrollTrigger.create({ trigger: el.parentElement, start: 'top 15%', end: 'bottom 70%', pin: el, pinSpacing: false });
      return;
    }

    gsap.set(el, { autoAlpha: 1 });
  }

  /* --------------------------------------------------- default targets */
  // Elements without data-anim still get subtle motion so old sections feel alive.
  function applyDefaults(root) {
    var sel = [
      '.section h1:not([data-anim])', '.section h2:not([data-anim])', '.section .heading:not([data-anim])',
      '.rte > p:not([data-anim])', '.product-item:not([data-anim])', '.collection-item:not([data-anim])',
      '.blog-post-item:not([data-anim])', '.image-with-text__image:not([data-anim])', '.btn:not([data-anim]):not(.no-anim)'
    ].join(',');
    root.querySelectorAll(sel).forEach(function (el) {
      if (el.closest('[data-anim]') || el.closest('[data-no-anim]') || el.closest('.header') || el.closest('.drawer, .modal, .quick-cart')) return;
      el.dataset.anim = el.matches('h1,h2,.heading') ? 'words' : 'fade-up';
    });
  }

  /* ---------------------------------------------------- hover helpers */
  function hoverFX(root) {
    if (isTouch) return;
    root.querySelectorAll('[data-magnetic]').forEach(function (el) {
      if (el.dataset.magDone) return; el.dataset.magDone = '1';
      var s = D(el.dataset.magnetic, 0.35);
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        gsap.to(el, { x: (e.clientX - r.left - r.width / 2) * s, y: (e.clientY - r.top - r.height / 2) * s, duration: 0.4, ease: 'power2.out' });
      });
      el.addEventListener('mouseleave', function () { gsap.to(el, { x: 0, y: 0, duration: 0.7, ease: 'elastic.out(1,0.4)' }); });
    });
    root.querySelectorAll('[data-tilt]').forEach(function (el) {
      if (el.dataset.tiltDone) return; el.dataset.tiltDone = '1';
      var max = D(el.dataset.tilt, 8);
      el.style.transformStyle = 'preserve-3d';
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        gsap.to(el, { rotateY: px * max, rotateX: -py * max, transformPerspective: 900, duration: 0.5, ease: 'power2.out' });
      });
      el.addEventListener('mouseleave', function () { gsap.to(el, { rotateY: 0, rotateX: 0, duration: 0.8, ease: 'power3.out' }); });
    });
    root.querySelectorAll('[data-hover-scale]').forEach(function (el) {
      if (el.dataset.hsDone) return; el.dataset.hsDone = '1';
      var s = D(el.dataset.hoverScale, 1.04);
      el.addEventListener('mouseenter', function () { gsap.to(el, { scale: s, duration: 0.45, ease: 'power2.out' }); });
      el.addEventListener('mouseleave', function () { gsap.to(el, { scale: 1, duration: 0.6, ease: 'power3.out' }); });
    });
  }

  /* --------------------------------------------------------- header */
  function header() {
    var h = document.querySelector('[data-header], .header, #header');
    if (!h) return;
    ScrollTrigger.create({
      start: 'top -80', end: 99999,
      onUpdate: function (self) {
        h.classList.toggle('is-scrolled', self.scroll() > 80);
      }
    });
  }

  /* ------------------------------------------------------ page load */
  function intro() {
    var tl = gsap.timeline();
    tl.to('.m-loader', { yPercent: -100, duration: 0.9, ease: 'power4.inOut', delay: 1.1 }, 0)
      .from('.header', { y: -30, autoAlpha: 0, duration: 0.8, ease: 'power3.out' }, 1.4);
    return tl;
  }

  /* ------------------------------------------------------------ init */
  function scan(root) {
    root = root || document;
    applyDefaults(root);
    root.querySelectorAll('[data-anim]').forEach(animate);
    hoverFX(root);
    ScrollTrigger.refresh();
  }

  /* extras */
  function extras() {
    // scroll progress
    var bar = document.createElement('div'); bar.className = 'm-progress'; document.body.appendChild(bar);
    gsap.to(bar, { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: 0.3 } });
    // custom cursor (desktop only)
    if (!isTouch && cfg.cursor !== false) {
      var dot = document.createElement('div'), ring = document.createElement('div');
      dot.className = 'm-cursor'; ring.className = 'm-cursor m-cursor--ring'; document.body.appendChild(dot); document.body.appendChild(ring);
      var qx = gsap.quickTo(dot, 'x', { duration: 0.12 }), qy = gsap.quickTo(dot, 'y', { duration: 0.12 });
      var rx = gsap.quickTo(ring, 'x', { duration: 0.45, ease: 'power3' }), ry = gsap.quickTo(ring, 'y', { duration: 0.45, ease: 'power3' });
      window.addEventListener('mousemove', function (e) { html.classList.add('has-cursor'); qx(e.clientX); qy(e.clientY); rx(e.clientX); ry(e.clientY); }, { passive: true });
      document.addEventListener('mouseover', function (e) { var h = e.target.closest('a, button, [data-tilt], .s786-swatch, input, select, textarea'); dot.classList.toggle('is-hover', !!h); ring.classList.toggle('is-hover', !!h); });
      document.addEventListener('mouseleave', function () { html.classList.remove('has-cursor'); });
    }
    // page transition curtain for internal links
    var curtain = document.createElement('div'); curtain.className = 'm-curtain'; document.body.appendChild(curtain);
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href]'); if (!a || isEditor) return;
      var href = a.getAttribute('href'); if (!href || href[0] === '#' || a.target === '_blank' || e.metaKey || e.ctrlKey || a.hasAttribute('download')) return;
      var u; try { u = new URL(a.href, location.href); } catch (x) { return; }
      if (u.origin !== location.origin || /\/(cart|checkout|account)/.test(u.pathname) && u.pathname === location.pathname) return;
      if (u.pathname === location.pathname && u.search === location.search) return;
      e.preventDefault();
      gsap.to(curtain, { yPercent: -100, duration: 0.55, ease: 'power4.inOut', onComplete: function () { location.href = a.href; } });
    });
    window.addEventListener('pageshow', function (ev) { if (ev.persisted) gsap.set(curtain, { yPercent: 100 }); });
    // continuous float for [data-float]
    document.querySelectorAll('[data-float]').forEach(function (el, i) {
      gsap.to(el, { y: (i % 2 ? 8 : -10), rotation: (i % 2 ? 1 : -1), duration: 3.2 + (i % 3) * 0.6, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    });
  }

  function init() {
    header();
    intro();
    extras();
    scan(document);
    // Theme editor re-renders sections
    document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
    document.addEventListener('shopify:section:reorder', function () { ScrollTrigger.refresh(); });
    // Lazy content / images resizing
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
    new MutationObserver(function (m) {
      var added = false;
      m.forEach(function (r) { if (r.addedNodes.length) added = true; });
      if (added) { clearTimeout(scan._t); scan._t = setTimeout(function () { scan(document); }, 120); }
    }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
  }

  window.Motion = { scan: scan, animate: animate, lenis: lenis, gsap: gsap, ScrollTrigger: ScrollTrigger, splitText: splitText };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
